// Default landing page content model. This mirrors the original
// "Vote YES on Measure H" campaign site. Used as the seed / fallback content.
// Later this is replaced by content fetched from the backend.

export const defaultContent = {
  theme: {
    navy: "#1c2a5e",
    red: "#c33641",
    lightblue: "#b9c1d4",
  },
  header: {
    logoUrl: "/logo-yes-on-o.png",
    donateUrl: "https://onlinecampaigntools.com/wf/VlhEUG5Fcw",
    donateImage: "/donate-button.png",
    signTop: "Eastern Sierra Unified School District",
    signYes: "YES",
    signOn: "on",
    signH: "O",
    signBottom: "WITHOUT INCREASING TAXES",
    endorseText: "Endorse Measure H",
    endorseUrl: "https://forms.gle/avHmi1QzzWRc2AWS8",
    instagramUrl: "https://www.instagram.com/voteyesono/",
    facebookUrl: "https://www.facebook.com/VoteYESonOforLocalKids/",
  },
  sections: [
    {
      id: "sec-hero",
      type: "hero",
      imageUrl:
        "https://images.unsplash.com/photo-1583468991267-3f068b607ae1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1905",
    },
    {
      id: "sec-banner-1",
      type: "banner",
      bg: "var(--red)",
      color: "#ffffff",
      text:
        "Vote YES on Measure H to repair and upgrade classrooms and school facilities in the Jamul-Dulzura Union School District without increasing existing tax rates.",
    },
    {
      id: "sec-text-1",
      type: "richtext",
      bg: "#ffffff",
      color: "#1c2a5e",
      paragraphs: [
        "Some of our classrooms and school facilities were built so long ago that underground pipes and infrastructure are failing, resulting in gas leaks, water leaks, and sewage backups. We need to fix these problems. <b><i>Out of 61 roofs across our schools, 58 are leaking and need replacement, and five portable classrooms are more than 25 years old, falling apart, and plagued by mold.</i></b>",
        "Measure H requires a transparent system of accountability, including a project list describing how the money will be spent, a citizens\u2019 oversight committee, and independent audits and public disclosure of how all funds are spent.",
        "Measure H will give the Jamul-Dulzura Union School District <b><i>access to millions of dollars in State matching funds</i></b> that we would otherwise not qualify for, maximizing the impact of the community\u2019s investment in our schools.",
      ],
    },
    {
      id: "sec-bullets-1",
      type: "bullets",
      bg: "var(--lightblue)",
      color: "#1c2a5e",
      groups: [
        {
          heading: "Vote YES on H to:",
          items: [
            "Replace wood and support beams that have extensive termite damage and dry rot",
            "Repair or replace deteriorating roofs, plumbing, gas lines, sewer lines, and electrical systems",
            "Renovate classrooms and facilities to support high-quality instruction in math, science, reading, writing, and technology",
            "Upgrade older classrooms to meet current health codes and building safety codes",
            "Improve student safety and campus security systems, including fencing, cameras, fire alarms, smoke detectors, and emergency communication systems",
          ],
        },
        {
          heading: "Measure H has built-in taxpayer safeguards:",
          items: [
            "<u>Will not increase existing tax rates</u>",
            "All money stays local to repair JDUSD classrooms and facilities",
            "Independent citizens\u2019 oversight and annual audits",
            "No funds can be used for administrator salaries or pensions",
          ],
        },
      ],
    },
    {
      id: "sec-text-2",
      type: "richtext",
      bg: "#ffffff",
      color: "#1c2a5e",
      paragraphs: [
        "Even if you don\u2019t have school-age children, improving local schools is a wise investment that protects our quality of life and our property values.",
      ],
    },
    {
      id: "sec-banner-2",
      type: "banner",
      bg: "var(--red)",
      color: "#ffffff",
      text:
        "Vote YES on H to support high-quality education in Jamul-Dulzura Union School District schools \u2013 without increasing taxes!",
    },
    {
      id: "sec-election",
      type: "election",
      bg: "#ffffff",
      color: "#1c2a5e",
      heading: "Election Information",
      items: [
        { label: "Election Date:", value: "November 3, 2026" },
        { label: "Look for Your Ballot:", value: "Week of October 5, 2026" },
        {
          label: "Voter Registration:",
          value:
            "You can register to vote by visiting RegisterToVote.ca.gov",
        },
        {
          label: "Ballots Are Due:",
          value: "Mail-in ballots must be postmarked by November 3, 2026",
        },
      ],
      footnote:
        "By November 3rd, vote YES on H for Jamul-Dulzura Union School District schools!",
    },
    {
      id: "sec-faq",
      type: "faq",
      bg: "var(--lightblue)",
      color: "#1c2a5e",
      heading: "Frequently Asked Questions about Measure H",
      items: [
        {
          q: "What is Measure H?",
          a: "Measure H on the November 3, 2026 ballot will provide dedicated, locally controlled funding to repair and update Jamul-Dulzura Union School District (JDUSD) schools and facilities to meet current safety and academic standards. All funds generated by Measure H must be used in our local schools and cannot be taken by the state.",
        },
        {
          q: "How are schools in the Jamul-Dulzura Union School District performing?",
          a: "Our teachers and staff work hard every day to deliver a high-quality education. However, aging and deteriorating facilities make it harder to provide students with safe, modern learning environments. Measure H helps ensure our facilities match the quality of instruction our students deserve.",
        },
        {
          q: "Why do we need Measure H?",
          a: "Many of our classrooms and facilities are decades old. Out of 61 roofs, 58 are leaking, underground pipes are failing, and several portable classrooms are more than 25 years old and plagued by mold. Measure H provides the funding needed to make these critical repairs.",
        },
        {
          q: "How will voting Yes on Measure H improve Jamul-Dulzura schools?",
          a: "Measure H will repair and replace deteriorating roofs, plumbing, gas lines, sewer lines, and electrical systems; renovate classrooms; upgrade facilities to meet health and safety codes; and improve student safety and campus security systems.",
        },
        {
          q: "Is Measure H the only way to update our schools?",
          a: "Local funding measures like Measure H are one of the only ways to secure dedicated, locally controlled funding that the state cannot take away. Measure H also unlocks State matching funds the district would otherwise not qualify for.",
        },
        {
          q: "How do we know Measure H funds will be spent wisely?",
          a: "Measure H requires a specific project list, an independent citizens\u2019 oversight committee, and annual independent audits with public disclosure of how all funds are spent.",
        },
        {
          q: "Has JDUSD already passed a school improvement bond measure?",
          a: "The district has worked to be a responsible steward of taxpayer dollars. Measure H is focused specifically on the urgent repairs and upgrades our aging facilities now require.",
        },
        {
          q: "How much would Measure H cost?",
          a: "Measure H is structured so that it will not increase existing tax rates. All money stays local to repair JDUSD classrooms and facilities.",
        },
        {
          q: "What is the difference between assessed value and market value?",
          a: "Assessed value is the value used by the county for tax purposes, which is often lower than market value (the price a property would sell for). Property tax calculations are based on assessed value, not market value.",
        },
        {
          q: "Will Measure H increase the taxes homeowners are currently paying?",
          a: "No. Measure H is designed so that it will not increase existing tax rates.",
        },
        {
          q: "How can I be sure funds from Measure H will be spent properly?",
          a: "An independent citizens\u2019 oversight committee and mandatory annual audits ensure transparency and accountability for every dollar spent.",
        },
        {
          q: "May funds from Measure H be used for administrator salaries and pensions?",
          a: "No. By law, no Measure H funds can be used for administrator salaries or pensions. All money stays local for classroom and facility repairs.",
        },
        {
          q: "I don\u2019t have children attending local schools. How does this impact me?",
          a: "Even if you don\u2019t have school-age children, improving local schools is a wise investment that protects our quality of life and our property values.",
        },
        {
          q: "When will Measure H appear on the ballot?",
          a: "Measure H will appear on the November 3, 2026 ballot.",
        },
        {
          q: "How can I register to vote or learn more about voting?",
          a: "You can register to vote by visiting RegisterToVote.ca.gov. Ballots will be mailed the week of October 5, 2026 and must be postmarked by November 3, 2026.",
        },
      ],
    },
    {
      id: "sec-footer",
      type: "footerlogo",
      bg: "#ffffff",
      tagline: "Paid for by Yes on O for ESUSD",
    },
  ],
};

export const SECTION_LABELS = {
  hero: "Hero Image",
  banner: "Color Banner",
  richtext: "Text Block",
  bullets: "Bullet List",
  election: "Election Info",
  faq: "FAQ Accordion",
  footerlogo: "Footer Logo",
};

export function newSection(type) {
  const id = `sec-${type}-${Date.now()}`;
  switch (type) {
    case "hero":
      return {
        id,
        type,
        imageUrl:
          "https://images.unsplash.com/photo-1577896851231-70ef18881754?crop=entropy&cs=srgb&fm=jpg&q=85&w=1905",
        images: [
          "https://images.unsplash.com/photo-1577896851231-70ef18881754?crop=entropy&cs=srgb&fm=jpg&q=85&w=1905",
        ],
      };
    case "banner":
      return {
        id,
        type,
        bg: "var(--red)",
        color: "#ffffff",
        text: "Add your headline here \u2013 double-click to edit.",
      };
    case "richtext":
      return {
        id,
        type,
        bg: "#ffffff",
        color: "#1c2a5e",
        paragraphs: ["Add your paragraph here. Click to edit this text."],
      };
    case "bullets":
      return {
        id,
        type,
        bg: "var(--lightblue)",
        color: "#1c2a5e",
        groups: [
          {
            heading: "New list heading:",
            items: ["First bullet point", "Second bullet point"],
          },
        ],
      };
    case "election":
      return {
        id,
        type,
        bg: "#ffffff",
        color: "#1c2a5e",
        heading: "Election Information",
        items: [{ label: "Label:", value: "Value" }],
        footnote: "Add a closing line here.",
      };
    case "faq":
      return {
        id,
        type,
        bg: "var(--lightblue)",
        color: "#1c2a5e",
        heading: "Frequently Asked Questions",
        items: [{ q: "Your question?", a: "Your answer goes here." }],
      };
    case "footerlogo":
      return { id, type, bg: "#ffffff", tagline: "Paid for by ..." };
    default:
      return { id, type };
  }
}
